import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PrototypeCustomDomainDto,
  WorkspaceDomainPrototypeDto,
  WorkspaceDomainPrototypeState,
  WorkspacePrototypeDomainDto,
} from '@aiai/shared';
import {
  deleteWorkspaceDomain,
  fetchWorkspaceDomain,
  fetchWorkspaceEntitlements,
  putWorkspaceDomain,
  reapplyWorkspaceDomain,
  verifyWorkspaceDomain,
} from '../api';
import { errorMessage } from '../lib/api';
import { Badge, type Tone } from './Badge';
import { Callout } from './Callout';
import { ConfirmDialog } from './ConfirmDialog';
import { SectionCard } from './SectionCard';
import { Spinner } from './Spinner';

const STATUS_TONE: Record<PrototypeCustomDomainDto['status'], Tone> = {
  pending: 'warning',
  active: 'success',
  failed: 'danger',
};
const DOMAIN_KEY = ['workspaceDomain'] as const;

/** Why a prototype is not (yet) on the branded host, in owner terms. */
const STATE_LABEL: Record<Exclude<WorkspaceDomainPrototypeState, 'branded'>, string> = {
  pending: 'still on its platform URL. Re-apply to move it',
  deploying: 'deploying. It moves when the deploy finishes',
  failed: 'last deploy or relabel failed',
  not_deployed: 'never deployed successfully',
  stopped: 'stopped. It moves when started again',
};

/**
 * Owner card for the workspace prototype domain: every prototype is served at
 * `<slug>.<domain>` once one wildcard CNAME points at the platform edge.
 * Hidden unless the workspace has the workspace prototype domain feature.
 */
export function WorkspaceDomainCard({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  const entQuery = useQuery({
    queryKey: ['orgEntitlements', orgId],
    queryFn: () => fetchWorkspaceEntitlements(orgId),
  });
  const entitled = entQuery.data?.features.workspacePrototypeDomain.enabled === true;
  const domainQuery = useQuery({
    queryKey: [...DOMAIN_KEY, orgId],
    queryFn: fetchWorkspaceDomain,
    enabled: entitled,
  });

  const apply = (dto: WorkspacePrototypeDomainDto) => {
    queryClient.setQueryData([...DOMAIN_KEY, orgId], dto);
    // Prototype URLs flip as relabels land: refetch lists lazily.
    queryClient.invalidateQueries({ queryKey: ['prototypes'] });
  };
  const putMutation = useMutation({
    mutationFn: (domain: string) => putWorkspaceDomain(domain),
    onSuccess: (dto) => {
      setInput('');
      apply(dto);
    },
  });
  const verifyMutation = useMutation({ mutationFn: verifyWorkspaceDomain, onSuccess: apply });
  const reapplyMutation = useMutation({
    mutationFn: reapplyWorkspaceDomain,
    // Relabels take seconds each; refresh the status list shortly after.
    onSuccess: () => {
      window.setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [...DOMAIN_KEY, orgId] });
        queryClient.invalidateQueries({ queryKey: ['prototypes'] });
      }, 5000);
    },
  });
  const removeMutation = useMutation({
    mutationFn: deleteWorkspaceDomain,
    onSuccess: () => {
      setConfirmRemove(false);
      apply({ domain: null, cnameTarget: domainQuery.data?.cnameTarget ?? null, prototypes: [] });
    },
  });

  if (!entitled) return null;

  const current = domainQuery.data?.domain ?? null;
  const target = domainQuery.data?.cnameTarget ?? null;
  const actionError = putMutation.error ?? verifyMutation.error ?? reapplyMutation.error;
  const prototypes = domainQuery.data?.prototypes ?? [];
  const notBranded = prototypes.filter((p) => p.state !== 'branded');

  return (
    <SectionCard title="Prototype domain">
      <div className="space-y-4">
        <p className="text-xs text-ink-muted">
          Serve every prototype in this workspace on your own domain, e.g.{' '}
          <code className="font-mono">&lt;prototype&gt;.app.yourcompany.com</code>. Certificates
          are issued automatically.
        </p>

        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const value = input.trim();
            if (value) putMutation.mutate(value);
          }}
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={current?.domain ?? 'app.yourcompany.com'}
            className="input flex-1 font-mono"
            aria-label="Prototype domain"
          />
          <button type="submit" className="btn-secondary" disabled={!input.trim() || putMutation.isPending}>
            {putMutation.isPending ? <Spinner className="h-3.5 w-3.5" /> : 'Save'}
          </button>
        </form>

        {actionError != null && (
          <Callout tone="danger">{errorMessage(actionError, 'Domain action failed')}</Callout>
        )}

        {current && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-ink">{current.domain}</span>
              <Badge tone={STATUS_TONE[current.status]}>{current.status}</Badge>
            </div>
            {target && (
              <div className="rounded-md border border-line bg-surface-2 p-3 text-xs">
                <p className="mb-2 text-ink-muted">Add this DNS record at your DNS provider:</p>
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono">
                  <span className="text-ink-faint">Name</span>
                  <span className="text-ink">*.{current.domain}</span>
                  <span className="text-ink-faint">Type</span>
                  <span className="text-ink">CNAME</span>
                  <span className="text-ink-faint">Value</span>
                  <span className="flex items-center gap-2 text-ink">
                    {target}
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      onClick={() => navigator.clipboard?.writeText(target)}
                    >
                      Copy
                    </button>
                  </span>
                </div>
              </div>
            )}
            {current.status === 'failed' && current.lastError && (
              <Callout tone="danger">{current.lastError}</Callout>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => verifyMutation.mutate()}
                disabled={verifyMutation.isPending}
              >
                {verifyMutation.isPending && <Spinner className="h-3.5 w-3.5" />}
                Check again
              </button>
              <button type="button" className="btn-ghost btn-sm" onClick={() => setConfirmRemove(true)}>
                Remove
              </button>
            </div>
            {current.status === 'active' && prototypes.length > 0 && (
              <BrandedStatus
                prototypes={prototypes}
                notBranded={notBranded}
                reapplying={reapplyMutation.isPending}
                queued={reapplyMutation.data?.queued ?? null}
                onReapply={() => reapplyMutation.mutate()}
              />
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title="Remove the prototype domain?"
        message="Every prototype in this workspace goes back to its platform URL."
        confirmLabel="Remove domain"
        busy={removeMutation.isPending}
        error={removeMutation.error ? errorMessage(removeMutation.error, 'Could not remove') : null}
        onConfirm={() => removeMutation.mutate()}
        onClose={() => setConfirmRemove(false)}
      />
    </SectionCard>
  );
}

/** How many prototypes actually serve on the branded host, and why the rest don't. */
function BrandedStatus({
  prototypes,
  notBranded,
  reapplying,
  queued,
  onReapply,
}: {
  prototypes: WorkspaceDomainPrototypeDto[];
  notBranded: WorkspaceDomainPrototypeDto[];
  reapplying: boolean;
  queued: number | null;
  onReapply: () => void;
}) {
  const branded = prototypes.length - notBranded.length;
  return (
    <div className="space-y-2 rounded-md border border-line p-3 text-xs">
      <p className="text-ink">
        {branded} of {prototypes.length} prototype{prototypes.length === 1 ? '' : 's'} serving on this
        domain.
      </p>
      {notBranded.length > 0 && (
        <ul className="space-y-1">
          {notBranded.map((p) => (
            <li key={p.projectId} className="text-ink-muted">
              <span className="font-mono text-ink">{p.brandedUrl.replace(/^https?:\/\//, '')}</span>{' '}
              ({p.name}): {STATE_LABEL[p.state as Exclude<WorkspaceDomainPrototypeState, 'branded'>]}
              {p.detail && <span className="block text-danger">{p.detail}</span>}
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <button type="button" className="btn-secondary btn-sm" onClick={onReapply} disabled={reapplying}>
          {reapplying && <Spinner className="h-3.5 w-3.5" />}
          Re-apply to all prototypes
        </button>
        {queued != null && (
          <span className="text-ink-faint">
            Re-applying to {queued} prototype{queued === 1 ? '' : 's'}…
          </span>
        )}
      </div>
    </div>
  );
}
